// 📄 pages/Proyectos/components/ComunicacionSystemModal.tsx
// Sub-fase K5: alta/edición de un Communication System del pool — referencia
// obligatoria a un Communication User ya creado (ver comunicacionService.ts,
// K4). Si el pool de Users está vacío, no se puede crear ninguno todavía.
import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import {
  ComunicacionSystem, ComunicacionSystemData, ComunicacionUser, entornosVaciosSystem
} from '../../../services/comunicacionService.ts';

interface Props {
  sistema?: ComunicacionSystem | null;
  usuarios: ComunicacionUser[];
  onSave: (data: ComunicacionSystemData) => Promise<void>;
  onClose: () => void;
}

const AMBIENTES = ['DEV', 'QAS', 'PRD'] as const;

export default function ComunicacionSystemModal({ sistema, usuarios, onSave, onClose }: Props) {
  const [nombre, setNombre] = useState(sistema?.nombre || '');
  const [comunicacionUserId, setComunicacionUserId] = useState(sistema?.comunicacionUserId || usuarios[0]?.id || '');
  const [descripcion, setDescripcion] = useState(sistema?.descripcion || '');
  const [entornos, setEntornos] = useState(sistema?.entornos || entornosVaciosSystem());
  const [ambienteActivo, setAmbienteActivo] = useState<typeof AMBIENTES[number]>('DEV');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!comunicacionUserId) { setError('Seleccioná un Communication User.'); return; }
    setGuardando(true);
    try {
      await onSave({ nombre, comunicacionUserId, descripcion, entornos });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el Communication System.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{sistema ? 'Editar' : 'Nuevo'} Communication System</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {usuarios.length === 0 ? (
          <p className="text-xs text-amber-400">
            Todavía no hay ningún Communication User creado. Creá al menos uno primero — todo System necesita un Inbound/Outbound User asociado.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Nombre lógico</label>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="ej. S4H_SLP_SYSTEM"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Communication User</label>
              <select
                value={comunicacionUserId}
                onChange={(e) => setComunicacionUserId(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.entornos.DEV.nombre || u.descripcion || u.id} ({u.tipo})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Descripción</label>
              <input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <div className="flex gap-1.5 mb-2">
                {AMBIENTES.map((amb) => (
                  <button
                    key={amb}
                    type="button"
                    onClick={() => setAmbienteActivo(amb)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      ambienteActivo === amb ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white border border-transparent'
                    }`}
                  >
                    {amb}
                  </button>
                ))}
              </div>
              <div className="space-y-2 bg-black/20 border border-white/10 rounded-xl p-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Business System ID ({ambienteActivo})</label>
                  <input
                    value={entornos[ambienteActivo].businessSystemId}
                    onChange={(e) => setEntornos({ ...entornos, [ambienteActivo]: { ...entornos[ambienteActivo], businessSystemId: e.target.value } })}
                    placeholder="ej. 744749172-T"
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">URL ({ambienteActivo})</label>
                  <input
                    value={entornos[ambienteActivo].url}
                    onChange={(e) => setEntornos({ ...entornos, [ambienteActivo]: { ...entornos[ambienteActivo], url: e.target.value } })}
                    placeholder="https://..."
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Notas ({ambienteActivo})</label>
                  <input
                    value={entornos[ambienteActivo].notas}
                    onChange={(e) => setEntornos({ ...entornos, [ambienteActivo]: { ...entornos[ambienteActivo], notas: e.target.value } })}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
